import { useState, useEffect } from 'react';
import { useConfig } from '../context/ConfigContext';
import { ADMIN_COLORS } from '../constants/bookingConst';
import BrandLogo from './BrandLogo';
import '../css/AdminSidebar.css';

// Nav drawer restyled to match the jassen reference (see
// jassen-files/sidebar-ui.png): brand row up top, a small uppercase
// section label, full-width rows with a solid pill highlight on the
// active item. Colors come from the shared ADMIN_COLORS palette
// (constants/bookingConst.js), which itself references the CSS custom
// properties the admin's Theme tab controls, so editing the palette in
// Config -> Theme updates this drawer too — not just the grid view.
const COLORS = ADMIN_COLORS;

// `configAdminOnly` entries are filtered out for anyone whose account
// isn't in CONFIG_ADMIN_EMAILS (see AdminPage.jsx's isConfigAdmin) — Config
// reshapes the whole deployment (sports, courts, pricing, branding,
// payment details), so it stays with the owner account rather than every
// staff login.
//
// `configWriterOnly` entries are narrower still: the screens that SAVE
// /config, which the database rules let only super-admins write (see
// SUPER_ADMIN_EMAILS in bookingConst.js and isSuperAdmin in authServiceUtil.js).
const menuItems = [
  { id: 'booking', icon: '📅', label: 'Booking', description: 'Manage bookings' },
  { id: 'openplay', icon: '🙌', label: 'Open Play', description: 'Review join requests'},
  { id: 'equipment', icon: '🎾', label: 'Equipment', description: 'Rentals & inventory', configAdminOnly: true  },
  { id: 'export', icon: '📊', label: 'Export', description: 'Generate reports' },
  { id: 'events', icon: '🎉', label: 'Events and Packages', description: 'Bookable event packages', configWriterOnly: true },
  { id: 'promo', icon: '🏷️', label: 'Promo', description: 'Limited-time rates', configWriterOnly: true },
  { id: 'audit', icon: '🕓', label: 'Audit Trail', description: 'Track booking activity' },
  // Diagnostic rather than operational — it reports on the app itself, not on
  // bookings — so it sits with Config behind the owner account. Club staff
  // have no use for a stack trace, and the entries carry page URLs and admin
  // emails from every session.
  { id: 'errorlogs', icon: '🛠️', label: 'Error Logs', description: 'Console errors by day', configAdminOnly: true },
  { id: 'config', icon: '⚙️', label: 'Config', description: 'App settings', configWriterOnly: true },
  { id: 'user', icon: '👤', label: 'User', description: 'Profile & password' },
];

function AdminSidebar({ activeScreen, onScreenChange, isOpen, onClose, onLogout, isConfigAdmin = false, isConfigWriter = false }) {
  const { config } = useConfig();
  const [isMobile, setIsMobile] = useState(false);
  const [hoveredId, setHoveredId] = useState(null);

  // Detect mobile screen
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };

    // Check on mount
    checkMobile();

    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Dynamic styles based on screen size
  const sidebarStyle = isMobile ? {
    width: '85%',
    maxWidth: '260px',
  } : {
    width: '260px',
    maxWidth: '260px',
  };

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="sidebar-backdrop"
          onClick={onClose}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(61, 18, 32, 0.45)',
            zIndex: 997,
          }}
        />
      )}

      {/* Sidebar */}
      <div
        className={`admin-sidebar ${isOpen ? 'open' : ''}`}
        style={{
          position: 'fixed',
          top: 0,
          height: '100vh',
          background: COLORS.surface,
          boxShadow: '-4px 0 25px rgba(61, 18, 32, 0.25)',
          zIndex: 998,
          transition: 'right 0.3s ease',
          display: 'flex',
          flexDirection: 'column',
          borderLeft: `1px solid ${COLORS.line}`,
          fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
          ...sidebarStyle,
        }}
      >
        {/* Brand row */}
        <div
          style={{
            padding: '1.25rem 1.25rem 1rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '0.5rem',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', minWidth: 0 }}>
            <BrandLogo
              src={config?.logoUrl}
              alt={config?.courtName || 'Court'}
              style={{ width: 34, height: 34 }}
            />
            {/* COLORS.ink (Config -> Theme -> Text colour), not COLORS.brand:
                the panel sits over the page surface rather than the header, so
                the club name reads as body text here. Following the brand
                colour instead left it illegible whenever a club themed its
                header separately from its text. */}
            <span
              style={{
                color: COLORS.ink, fontFamily: "'Archivo', sans-serif", fontWeight: 800,
                fontSize: '1.05rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}
            >
              {config?.courtName || 'Court Booking'}
            </span>
          </div>
          <button
            onClick={onClose}
            aria-label="Close menu"
            style={{
              background: 'none',
              border: 'none',
              color: COLORS.inkSoft,
              fontSize: '1.1rem',
              cursor: 'pointer',
              padding: '0.25rem',
              lineHeight: 1,
              flex: '0 0 auto',
            }}
          >
            ✕
          </button>
        </div>

        {/* Menu Items */}
        <nav style={{ flex: 1, padding: '0.5rem 0.85rem', overflowY: 'auto' }}>
          <div
            style={{
              color: COLORS.inkFaint,
              fontSize: '0.68rem',
              fontWeight: 700,
              letterSpacing: '.12em',
              textTransform: 'uppercase',
              padding: '0.5rem 0.5rem 0.6rem',
            }}
          >
            Menu
          </div>

          {menuItems
            .filter(item => (!item.configAdminOnly || isConfigAdmin) && (!item.configWriterOnly || isConfigWriter))
            .map((item) => {
              const active = activeScreen === item.id;
              const hovered = hoveredId === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    onScreenChange(item.id);
                    onClose();
                  }}
                  onMouseEnter={() => setHoveredId(item.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  style={{
                    width: '100%',
                    padding: isMobile ? '0.7rem 0.9rem' : '0.75rem 1rem',
                    marginBottom: '0.3rem',
                    background: active ? COLORS.cyanDeep : (hovered ? COLORS.cyanSoft : 'transparent'),
                    border: 'none',
                    borderRadius: '12px',
                    color: active ? '#ffffff' : COLORS.ink,
                    textAlign: 'left',
                    cursor: 'pointer',
                    transition: 'background 0.16s ease, color 0.16s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.7rem',
                    fontFamily: 'inherit',
                  }}
                >
                  <span style={{ fontSize: isMobile ? '1rem' : '1.05rem', lineHeight: 1, flex: '0 0 auto' }}>{item.icon}</span>
                  <span style={{ display: 'flex', flexDirection: 'column', gap: '0.1rem', minWidth: 0 }}>
                    <span style={{ fontSize: isMobile ? '0.85rem' : '0.92rem', fontWeight: 600 }}>{item.label}</span>
                    <span
                      style={{
                        fontSize: isMobile ? '0.66rem' : '0.72rem',
                        fontWeight: 500,
                        color: active ? 'rgba(255, 255, 255, 0.8)' : COLORS.inkSoft,
                      }}
                    >
                      {item.description}
                    </span>
                  </span>
                </button>
              );
            })}
        </nav>

        {/* Footer - Logout */}
        <div
          style={{
            padding: isMobile ? '1rem' : '1.25rem',
            borderTop: `1px solid ${COLORS.line}`,
          }}
        >
          <button
            onClick={onLogout}
            style={{
              width: '100%',
              padding: isMobile ? '0.6rem 0.75rem' : '0.75rem 1rem',
              background: 'rgba(192, 57, 43, 0.1)',
              border: '1px solid rgba(192, 57, 43, 0.35)',
              borderRadius: '12px',
              color: '#c0392b',
              fontSize: isMobile ? '0.8rem' : '0.9rem',
              fontWeight: '700',
              fontFamily: "'Archivo', sans-serif",
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = 'rgba(192, 57, 43, 0.18)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = 'rgba(192, 57, 43, 0.1)';
            }}
          >
            Logout
          </button>
        </div>
      </div>
    </>
  );
}

export default AdminSidebar;
